import { MEMBER_SESSION_EXPIRED_EVENT } from './session-expiry.js';
/**
 * RealSessionProvider: BFF-backed adapter implementing `SessionPort`
 * (spec 023-identity-and-access-admins, T022 / FR-014 / D-105 / D-106; the
 * dual-audience collapse is 032-identity-and-access-members, RD-111 / T014-T015).
 *
 * The SOLE session source for BOTH audiences (RD-111). One BFF `session.me`
 * round-trip returns the role, so a member request resolves to a `member`
 * `AuthState` and an admin request to an `admin` `AuthState` from the same
 * provider — the `DualAudienceSessionProvider` composite that briefly paired
 * this with a demo member arm (D-106) has been COLLAPSED away (a two-arm
 * composite would double the round-trip for no benefit once `session.me`
 * itself carries the role). It keeps the small-class structure (a private
 * `#state` field, a listener `Set`, `subscribe`/notify-on-change, a `dispose()`
 * teardown) so `useSession()` consumers see no API change across the collapse.
 *
 * ## NO client token, the opaque-cookie BFF invariant (D-092 / D-105 / RD-002)
 *
 * This provider holds NO session token, NO bearer, NO credential of any
 * kind. The session cookie (`__Secure-oma_session`) is `HttpOnly`, so page
 * JavaScript CANNOT read it. Identity is learned ONLY by an authoritative
 * server round-trip (`session.me`), and the cookie rides same-origin
 * requests automatically, there is nothing for this class to store. The
 * absence of a token field is the load-bearing security property: a token
 * held in JS would be an XSS exfiltration target, which the BFF model
 * exists to eliminate. Do NOT add a token/bearer field here.
 *
 * ## `.tsx` with no JSX
 *
 * This file carries the `.tsx` extension to match the task descriptor's
 * stated path and the retired demo adapter's convention, but it contains
 * NO JSX, it is a plain class adapter. The TypeScript compiler treats it
 * identically to a `.ts` file when no JSX tokens appear.
 *
 * ## State machine
 *
 *   - `state` starts `{ kind: 'loading' }`, NOT anonymous. Identity is not yet
 *     known: the constructor kicks off an async `refresh()` (it does NOT block
 *     construction, a constructor cannot be async, and blocking the composition
 *     root on a network read would stall first paint), and the cookie-backed
 *     `session.me` round-trip has not resolved on the first render. The
 *     `loading` seed is the load-bearing fix for the admin-cutover redirect
 *     bug: if the seed were `anonymous`, `RouteGuard` would read "signed out"
 *     and bounce an already-authenticated admin to `/login` BEFORE `session.me`
 *     resolved to admin (and on every hard-refresh of a protected route). With
 *     a `loading` seed the guard instead WAITS (renders a placeholder) until
 *     resolution. `loading -> anonymous|admin` is a real transition, so
 *     `refresh()` notifies subscribers and consumers re-render once identity is
 *     known. This matches the contract: `subscribe` does not fire on
 *     registration, consumers read `.state` for the initial value.
 *   - `refresh()` calls `session.me`, maps the result to `AuthState`, and
 *     notifies only when the state actually changed (an admin→admin or
 *     anonymous→anonymous refresh is silent, so subscribers do not re-render
 *     for a no-op). A query rejection (network error) is tolerated: the
 *     provider stays on / falls back to anonymous and does NOT throw out of
 *     `refresh()`. There is no client logger in `apps/web`, so the rejection
 *     is swallowed deliberately (see the catch block).
 *   - `signOut()` conceals identity before POSTing `/auth/sign-out`. It becomes
 *     anonymous only after a successful POST and an anonymous `session.me`
 *     read; any failure keeps a retry state. Refreshes are ignored while a
 *     sign-out is pending or has failed, and stale refresh results are dropped.
 *   - There is NO sign-in method. Real sign-in is the server routes
 *     (`/login` for members, `/admin/login` for admins), reached by
 *     `RouteGuard` redirects and plain links with a `returnTo`; the post-login
 *     callback returns the browser to where it was. This provider only reads
 *     the resulting session back via `refresh()`.
 *   - `completeOnboarding()` is a no-op for both BFF audiences (onboarding is
 *     server-side at sign-in — admin profile creation, RD-105 member status —
 *     not a client wizard).
 *
 * ## Both audiences from one `session.me` (RD-111)
 *
 * `session.me` returns `{kind:'anonymous'} | {kind:'admin', …} | {kind:'member',
 * …}`. The member arm carries `email`, `displayName`, the server-snapshotted
 * `membershipStatus`, and the jäsennumero (`memberNumber`). The API's member arm
 * has landed, so `mapSessionMeToAuthState` narrows its member branch off the
 * derived `SessionMeResult` directly (no local widening).
 *
 * ## Wiring
 *
 * Wired active at the composition root as the SOLE `ports.session` (RD-111 /
 * T015): `useSessionProvider` constructs `new RealSessionProvider({ trpc })`
 * and provides it directly — no composite, no demo arm. `useSession()`
 * consumers see no API change; this adapter owns both audiences (the BFF cookie
 * + `session.me`).
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type { AuthState, SessionPort } from './SessionPort.js';
import { announceSessionRevocation } from './sessionInvalidation.js';
import { authStatesEqual, mapSessionMeToAuthState } from './sessionMeMapping.js';

type StateListener = (state: AuthState) => void;

// The `session.me` → `AuthState` type seam and the pure map/equality helpers
// live in `./sessionMeMapping.ts` (R3 + the 400-line ceiling): `SessionMeResult`
// (the derived API union, now carrying the member arm), `mapSessionMeToAuthState`,
// and `authStatesEqual`. Re-exported below so existing importers (the colocated
// `RealSessionProvider.test.tsx`) keep their import path.
export type { SessionMeResult } from './sessionMeMapping.js';

const ANONYMOUS: AuthState = Object.freeze({ kind: 'anonymous' });

/**
 * The "identity not yet known" seed. `#state` starts here (NOT anonymous) so a
 * consumer can distinguish "the BFF round-trip has not resolved" from "resolved
 * to signed-out", the whole point of the `loading` arm (see the class §State
 * machine and `RouteGuard`). The first `refresh()` replaces it wholesale.
 */
const LOADING: AuthState = Object.freeze({ kind: 'loading' });

/**
 * Same-origin sign-out endpoint. A `POST` (not a navigation) so the page
 * stays put while the cookie is cleared; the subsequent `refresh()` reads
 * the now-anonymous session.
 */
const SIGN_OUT_PATH = '/auth/sign-out';

/**
 * Constructor dependencies. Every external touch-point is injected so unit
 * tests substitute stubs and NO real network call happens.
 */
export interface RealSessionProviderDeps {
  /**
   * The typed tRPC client, narrowed to the `session` namespace this
   * provider actually consumes (`session.me.query()`). A `Pick` keeps the
   * dependency honest and lets a test pass a one-namespace stub.
   */
  readonly trpc: Pick<WebTrpcClient, 'session'>;
  /**
   * `fetch` implementation for the sign-out POST. Defaults to the global
   * `fetch`. Injected in tests so no real request leaves the process.
   */
  // `?: T | undefined` under `exactOptionalPropertyTypes`: production
  // callers omit it (falls back to the global `fetch`); tests pass a stub.
  readonly fetchImpl?: typeof fetch | undefined;
}

export class RealSessionProvider implements SessionPort {
  // Starts `loading` (identity not yet known), NOT anonymous: the constructor's
  // floating `refresh()` resolves it to anonymous|admin. See class §State
  // machine for why the seed is load-bearing (the admin-cutover redirect bug).
  #state: AuthState = LOADING;
  #listeners: Set<StateListener> = new Set();
  #disposed = false;
  #generation = 0;
  #signOutOperation: Promise<void> | undefined;
  readonly #handleMemberExpiry = (): void => {
    if (this.#state.kind !== 'admin') void this.refresh();
  };

  readonly #trpc: Pick<WebTrpcClient, 'session'>;
  readonly #fetchImpl: typeof fetch;

  constructor(deps: RealSessionProviderDeps) {
    this.#trpc = deps.trpc;
    this.#fetchImpl = deps.fetchImpl ?? globalThis.fetch.bind(globalThis);
    if (typeof window !== 'undefined')
      window.addEventListener(MEMBER_SESSION_EXPIRED_EVENT, this.#handleMemberExpiry);

    // Kick off the initial identity read WITHOUT blocking construction.
    // A constructor cannot be async, and stalling the composition root on
    // a network round-trip would delay first paint. The `void` marks the
    // floating promise as intentional; `refresh()` never rejects (it
    // swallows query failures), so there is no unhandled-rejection risk.
    void this.refresh();
  }

  get state(): AuthState {
    return this.#state;
  }

  async revalidate(): Promise<void> {
    if (this.#disposed || this.#state.kind === 'signingOut') return;
    if (this.#state.kind === 'signOutFailed') {
      // Another tab's confirmed sign-out ends this failed attempt too.
      const generation = ++this.#generation;
      try {
        const result = await this.#trpc.session.me.query();
        if (result.kind === 'anonymous' && !this.#disposed && generation === this.#generation) {
          this.#transitionTo(ANONYMOUS);
        }
      } catch {
        // The retry screen stays; the member can still retry the sign-out.
      }
      return;
    }
    if (this.#state.kind === 'member' || this.#state.kind === 'admin') this.#transitionTo(LOADING);
    await this.refresh();
  }

  invalidate(): void {
    if (this.#disposed) return;
    this.#generation += 1;
    if (this.#state.kind !== 'signingOut' && this.#state.kind !== 'signOutFailed') {
      this.#transitionTo(ANONYMOUS);
    }
  }

  /**
   * Whether `dispose()` has run. Read by the composition root's StrictMode-safe
   * lifecycle (`useSessionProvider`) to detect the dev-mode synthetic remount:
   * StrictMode runs the dispose effect's cleanup (which disposes this provider,
   * marking it `#disposed`) and then re-runs effect setup holding the SAME
   * `useState` instance. A disposed provider can never resolve its in-flight
   * `session.me` (the `#disposed` guard in `refresh()` drops the
   * `loading -> resolved` transition), so the root must observe disposal and
   * swap in a fresh provider. This accessor is the single observation seam;
   * nothing mutates through it. (Inherited from the dual-audience composite's
   * `isDisposed` at the RD-111 collapse — the bug and the fix are identical, the
   * source is just no longer wrapped.)
   */
  get isDisposed(): boolean {
    return this.#disposed;
  }

  /**
   * Re-read identity from the BFF and reconcile `#state`. Public because
   * the SPA re-checks identity after a sign-in callback redirect (member Telia
   * or admin Entra) lands back in the app (a fresh `session.me` against the
   * now-present cookie).
   *
   * Tolerant of a query rejection: a network failure resolves to `anonymous`
   * rather than throwing. This is load-bearing for the `loading` seed, a
   * rejection on the FIRST `refresh()` moves `loading -> anonymous`, so the app
   * NEVER hangs on `loading` (the guard's placeholder is transient by
   * construction). There is no client logger in `apps/web`, so the error is
   * swallowed deliberately, surfacing it would require a logging seam this app
   * does not have, and an unhandled throw out of the constructor's floating
   * `refresh()` would be a worse outcome than a silent fall-back to anonymous
   * (the SPA simply behaves as logged-out until the next read).
   */
  async refresh(): Promise<void> {
    if (
      this.#disposed ||
      this.#state.kind === 'signingOut' ||
      this.#state.kind === 'signOutFailed'
    ) {
      return;
    }
    const generation = ++this.#generation;
    let next: AuthState;
    try {
      const result = await this.#trpc.session.me.query();
      next = mapSessionMeToAuthState(result);
    } catch {
      // Network / transport failure → treat as anonymous. Swallowed by
      // design: no client logger exists in apps/web (see method JSDoc).
      next = ANONYMOUS;
    }
    if (this.#disposed || generation !== this.#generation) {
      return;
    }
    this.#transitionTo(next);
  }

  /**
   * Revocation failure keeps a credential-free retry state instead of claiming sign-out.
   * Broker single-sign-out remains outside this application-local revocation:
   * the mock broker has no end-session endpoint and the Telia front-channel
   * logout contract is unconfirmed (032 / FR-012).
   */
  signOut(): Promise<void> {
    this.#signOutOperation ??= this.#revoke().finally(() => {
      this.#signOutOperation = undefined;
    });
    return this.#signOutOperation;
  }

  async #revoke(): Promise<void> {
    if (this.#disposed) throw new Error('Session provider disposed');
    this.#generation += 1;
    try {
      this.#transitionTo({ kind: 'signingOut' });
      const response = await this.#fetchImpl(SIGN_OUT_PATH, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Session revocation failed');
      const confirmed = await this.#trpc.session.me.query();
      if (confirmed.kind !== 'anonymous') throw new Error('Session remains active');
    } catch (error) {
      if (!this.#disposed) this.#transitionTo({ kind: 'signOutFailed' });
      throw error;
    }
    if (!this.#disposed) {
      this.#transitionTo(ANONYMOUS);
      announceSessionRevocation();
    }
  }

  /**
   * No-op for the BFF audiences. Onboarding is performed server-side at
   * sign-in: admin profile creation is the sign-in upsert (FR-013), and a
   * real member's `onboardingCompleted` is server-derived and projected onto
   * `session.me` (RD-105/RD-111) — there is no client wizard for either real
   * audience (the demo member's tab-local wizard belonged to
   * `DemoSessionProvider`, now unwired). The method exists to satisfy
   * `SessionPort`; there is nothing for the real adapter to do here.
   */
  completeOnboarding(): void {
    // Intentionally empty: both BFF audiences onboard server-side at sign-in.
  }

  subscribe(listener: StateListener): () => void {
    this.#listeners.add(listener);

    // Idempotent unsubscribe: the `active` flag (captured per invocation)
    // means calling the returned function twice removes only the one
    // listener the first call registered and is a no-op thereafter,
    // matching the `SessionPort` contract.
    let active = true;
    return () => {
      if (!active) {
        return;
      }
      active = false;
      this.#listeners.delete(listener);
    };
  }

  /**
   * Tear down: mark disposed (so an in-flight `refresh()` that resolves
   * after disposal does not transition or notify a dead adapter) and clear
   * listeners. Idempotent. A pending `fetch`/`query` cannot be hard-
   * cancelled here without threading an `AbortController` through the
   * injected `fetch`/tRPC client (neither exposes a cancel handle at this
   * seam today), so the `#disposed` guard in `refresh()` is the cancel
   * mechanism: a late resolution is observed and dropped.
   */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    if (typeof window !== 'undefined')
      window.removeEventListener(MEMBER_SESSION_EXPIRED_EVENT, this.#handleMemberExpiry);
    this.#listeners.clear();
  }

  #transitionTo(nextState: AuthState): void {
    // Notify only on an actual change so an admin→admin / anonymous→
    // anonymous refresh is silent (no spurious re-render). Equality is
    // structural over the discriminated union's fields.
    if (authStatesEqual(this.#state, nextState)) {
      return;
    }
    this.#state = nextState;
    // Iterate a snapshot so a listener that unsubscribes itself during
    // dispatch does not perturb iteration. `Set` is insertion-ordered,
    // matching the "notify in registration order" contract.
    const snapshot = Array.from(this.#listeners);
    for (const listener of snapshot) {
      listener(nextState);
    }
  }
}
